import React from "react";
import { toast } from 'react-toastify';
import { useDealerContext } from "./DealerContext";
import useApi from "../../lib/apiService.js";

const VehicleContext = React.createContext()

const NONE = { status: "NONE", data: {} };
const LOADING = { status: "LOADING", data: {} };
const READY = (data) => ({ status: "READY", data: data });
const ERROR = { status: "ERROR", data: {} };

function VehicleContextProvider({ children }) {
    const Api = useApi();
    const dealer = useDealerContext();

    const [vaVehicle, setVaVehicle] = React.useState(NONE);
    const [cviSvsVehicle, setCviSvsVehicle] = React.useState(NONE);
    const [cvi2Vehicle, setCvi2Vehicle] = React.useState(NONE);
    const [cviChangeLog, setCviChangeLog] = React.useState(NONE);
    const [cviVehicleLogEntries, setCviVehicleLogEntries] = React.useState(NONE);
    const [mvsVehicle, setMvsVehicle] = React.useState(NONE);
    const [cvmMedia, setCvmMedia] = React.useState(NONE);
    const [iolVehicle, setIolVehicle] = React.useState(NONE);
    const [ddcVehicle, setDdcVehicle] = React.useState(NONE);
    const [vinVehicle, setVinVehicle] = React.useState(NONE);
    const [atcVehicle, setAtcVehicle] = React.useState(NONE);
    const [vaSrcVehicle, setVaSrcVehicle] = React.useState(NONE);
    const [vaDivaVehicle, setVaDivaVehicle] = React.useState(NONE);

    async function loadVaVehicle(vehicleId) {
        try {
            setVaVehicle(LOADING);
            setVaVehicle(READY(await Api.getVautoVehicleDetails(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle from vAuto:", e);
            toast.error("Problem getting vehicle from vAuto");
            setVaVehicle(ERROR);
        }
    }

    async function loadCviSvs(entityId, vehicleId) {
        try {
            setCviSvsVehicle(LOADING);
            setCviSvsVehicle(READY(await Api.getCviSellableVehicle(entityId, vehicleId)));
        } catch (e) {
            console.error("Problem getting sellable vehicle from CVI:", e);
            toast.error("Problem getting sellable vehicle from CVI");
            setCviSvsVehicle(ERROR);
        }
    }

    async function loadCvi2Vehicle(inventoryRecordId) {
        try {
            setCvi2Vehicle(LOADING);
            setCvi2Vehicle(READY(await Api.getCvi2InventoryRecord(inventoryRecordId)));
        } catch (e) {
            console.error("Problem getting inventory record from CVI 2.1:", e);
            toast.error("Problem getting inventory record from CVI 2.1");
            setCvi2Vehicle(ERROR);
        }
    }

    async function loadVaDiva(entityId, vin) {
        try {
            setVaDivaVehicle(LOADING);
            setVaDivaVehicle(READY(await Api.getCviVehicleFromDiva(entityId, vin)));
        } catch (e) {
            console.error("Problem getting va vehicle from diva:", e);
            toast.error("Problem getting va vehicle from diva");
            setVaDivaVehicle(ERROR);
        }
    }

    async function loadChangeLog(entityId, vehicleId) {
        try {
            setCviChangeLog(LOADING);
            setCviChangeLog(READY(await Api.getCviVehicleChangeLog(entityId, vehicleId)));
        } catch (e) {
            console.error("Problem getting change log from CVI:", e);
            toast.error("Problem getting change log from CVI");
            setCviChangeLog(ERROR);
        }            
    }

    async function loadLogEntries(vehicleId) {
        try {
            setCviVehicleLogEntries(LOADING);
            setCviVehicleLogEntries(READY(await Api.getCviVehicleLogEntries(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle log entries from CVI:", e);
            toast.error("Problem getting vehicle log entries from CVI");
            setCviVehicleLogEntries(ERROR);
        }
    }

    async function loadCvm(vehicleId) {
        try {
            setCvmMedia(LOADING);
            setCvmMedia(READY(await Api.getCviVehicleMedia(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle media from CVM:", e);
            toast.error("Problem getting vehicle media from CVM");
            setCvmMedia(ERROR);
        }
    }

    async function loadMvsVehicle(vehicleId) {
        try {
            setMvsVehicle(LOADING);
            setMvsVehicle(READY(await Api.getMvsVehicleDetails(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle from MVS:", e);
            toast.error("Problem getting vehicle from MVS");
            setMvsVehicle(ERROR);
        }
    }

    async function loadIolVehicle(vrtId, vehicleId) {
        try {
            setIolVehicle(LOADING);
            setIolVehicle(READY(await Api.getIolVehicleDetails(vrtId, vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle from IOL:", e);
            toast.error("Problem getting vehicle from IOL");
            setIolVehicle(ERROR);
        }
    }

    async function loadDdcVehicle(vehicleId) {
        try {
            setDdcVehicle(LOADING);
            setDdcVehicle(READY(await Api.getDdcVehicleDetails(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle from DDC:", e);
            toast.error("Problem getting vehicle from DDC");
            setDdcVehicle(ERROR);
        }
    }

    async function loadAtcVehicle(vehicleId) {
        try {
            setAtcVehicle(LOADING);
            setAtcVehicle(READY(await Api.getAtcVehicleDetails(vehicleId)));
        } catch (e) {
            console.error("Problem getting vehicle from ATC:", e);
            toast.error("Problem getting vehicle from ATC");
            setAtcVehicle(ERROR);
        }
    }

    const loadVehicle = async (params) => {
        let loading = [];

        if (params.cviVehicleId) {
            loading.push(loadCviSvs(dealer.state.entityId, params.cviVehicleId));
            loading.push(loadChangeLog(dealer.state.entityId, params.cviVehicleId));
            loading.push(loadLogEntries(params.cviVehicleId));
            loading.push(loadCvm(params.cviVehicleId));
            if (dealer.state.showInternal)
                loading.push(loadVaDiva(dealer.state.entityId, params.vin));
            else
                setVaDivaVehicle(NONE);
        } else {
            setCviSvsVehicle(NONE);
            setCviChangeLog(NONE);
            setCviVehicleLogEntries(NONE);
            setCvmMedia(NONE);
            setVaDivaVehicle(NONE);
        }

        if (params.vaVehicleId)
            loading.push(loadVaVehicle(params.vaVehicleId));
        else
            setVaVehicle(NONE);
        
        if (params.mvsVehicleId)
            loading.push(loadMvsVehicle(params.mvsVehicleId));
        else
            setMvsVehicle(NONE);

        if (params.iolVehicleId)
            loading.push(loadIolVehicle(dealer.state.iolVrtId, params.iolVehicleId));
        else
            setIolVehicle(NONE);

        if (params.ddcVehicleId)
            loading.push(loadDdcVehicle(params.ddcVehicleId));
        else 
            setDdcVehicle(NONE);

        if (params.cvi2VehicleId)
            loading.push(loadCvi2Vehicle(params.cvi2VehicleId));
        else
            setCvi2Vehicle(NONE);

        if (params.vinVehicle)
            setVinVehicle(READY(params.vinVehicle));
        else
            setVinVehicle(NONE);

        if (params.atcVehicleId)
            loading.push(loadAtcVehicle(params.atcVehicleId));
        else
            setAtcVehicle(NONE);

        if (params.vaSrcVehicle)
            setVaSrcVehicle(READY(params.vaSrcVehicle));
        else
            setVaSrcVehicle(NONE);

        await Promise.all(loading);
    }

    // NOTE: you *might* need to memoize this value
    // Learn more in http://kcd.im/optimize-context
    const value = {
        vaCoreVehicle: vaVehicle,
        vaMvsVehicle: mvsVehicle,
        cviSvsVehicle: cviSvsVehicle,
        cvi2Vehicle: cvi2Vehicle,
        cviChangeLog: cviChangeLog,
        cviVehicleLogEntries: cviVehicleLogEntries,
        cviCvmVehicle: cvmMedia,
        cviVaSrcVehicle: vaSrcVehicle,
        cviVaDivaVehicle: vaDivaVehicle,
        iolVehicle: iolVehicle,
        ddcVehicle: ddcVehicle,
        atcVehicle: atcVehicle,
        vinVehicle: vinVehicle,
        loadVehicle: loadVehicle
    }
    return <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>
}

function useVehicleContext() {
    const context = React.useContext(VehicleContext)
    if (context === undefined) {
        throw new Error('useVehicleContext must be used within a VehicleProvider');
    }
    return context;
}

export { VehicleContextProvider, useVehicleContext };